;(() => {
  const trapFocus = (container, event) => {
    const focusable = [
      ...container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
    ].filter((element) => !element.hidden && element.offsetParent !== null)

    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const initHeaderDisclosure = (disclosure) => {
    if (disclosure.dataset.initialized === 'true') return

    const toggle = disclosure.querySelector('[data-disclosure-toggle]')
    const list = disclosure.querySelector('[data-disclosure-list]')
    const input = disclosure.querySelector('[data-disclosure-input]')
    const form = disclosure.closest('form')

    if (!toggle || !list || !input || !form) return

    const positionList = () => {
      const rect = toggle.getBoundingClientRect()
      list.style.top = `${Math.round(rect.bottom + 8)}px`
      list.style.right = `${Math.round(window.innerWidth - rect.right)}px`
      list.style.left = 'auto'
    }

    const close = () => {
      toggle.setAttribute('aria-expanded', 'false')
      list.setAttribute('aria-hidden', 'true')
      list.classList.add('hidden')
      list.style.top = ''
      list.style.right = ''
      list.style.left = ''
    }

    const open = () => {
      positionList()
      toggle.setAttribute('aria-expanded', 'true')
      list.setAttribute('aria-hidden', 'false')
      list.classList.remove('hidden')
    }

    toggle.addEventListener('click', () => {
      const willOpen = toggle.getAttribute('aria-expanded') !== 'true'
      if (willOpen) open()
      else close()
    })

    list.querySelectorAll('[data-disclosure-option]').forEach((option) => {
      option.addEventListener('click', (event) => {
        event.preventDefault()
        input.value = option.dataset.value
        close()
        form.submit()
      })
    })

    document.addEventListener('click', (event) => {
      if (!disclosure.contains(event.target)) close()
    })

    window.addEventListener('resize', () => {
      if (toggle.getAttribute('aria-expanded') === 'true') positionList()
    })

    disclosure.dataset.initialized = 'true'
    close()
  }

  const initHeader = (header) => {
    if (header.dataset.initialized === 'true') return

    const drawer = header.querySelector('[data-drawer-menu]')
    const mobileHeaderRow = header.querySelector('.header__row-mobile')
    const lowerBar = header.querySelector('.header__row-desktop.lower')
    const announcementBar = document.querySelector('.announcement-bar__outer-wrapper')
    const headerSpace = document.querySelector('[data-header-space]')
    const menuButtons = header.querySelectorAll('[data-js-menu-button]')
    const overlay = header.querySelector('.header__overlay')
    const quickSearch = document.querySelector('[data-quick-search-desktop]')
    const transparent = header.dataset.transparentHeader === 'true'
    const enableStickyHeader = header.dataset.enableStickyHeader === 'true'

    // Early script sets this too; keep in sync for theme editor setting toggles.
    document.documentElement.classList.toggle('sticky-header-enabled', enableStickyHeader)
    document.body.classList.toggle('header-transparent', transparent)

    const positionOverlay = () => {
      if (overlay) overlay.style.top = `${Math.ceil(header.getBoundingClientRect().bottom)}px`
    }

    const setHeaderHeight = () => {
      const height = Math.ceil(header.getBoundingClientRect().height)
      const lowerBarHeight = lowerBar ? Math.ceil(lowerBar.getBoundingClientRect().height) : height
      const lowerBarOffset = Math.max(height - lowerBarHeight, 0)

      document.documentElement.style.setProperty('--height-header', `${height}px`)
      document.documentElement.style.setProperty('--header-desktop-sticky-position', `${lowerBarOffset * -1}px`)
      document.documentElement.style.setProperty('--header-desktop-sticky-height', `${height - lowerBarOffset}px`)

      if (transparent) {
        const main = document.getElementById('main')
        if (main) main.style.marginTop = `-${height}px`
      }

      if (header.classList.contains('dropdown-active')) positionOverlay()
    }

    const setTransparentState = () => {
      const useTransparent = transparent && window.scrollY === 0 && !header.classList.contains('dropdown-active')
      header.classList.toggle('bg-transparent', useTransparent)
      header.classList.toggle('text-[var(--color-text-transparent)]', useTransparent)
      header.classList.toggle('bg-[var(--color-background-header)]', !useTransparent)
      header.classList.toggle('text-[var(--color-text-header)]', !useTransparent)
      // Only swap logos when a dedicated transparent logo exists; otherwise keep
      // the regular logo visible so it doesn't disappear on transparent headers.
      const hasTransparentLogo = header.querySelector('.transparent-logo') !== null
      header.querySelectorAll('.regular-logo').forEach((logo) => logo.classList.toggle('hidden', useTransparent && hasTransparentLogo))
      header.querySelectorAll('.transparent-logo').forEach((logo) => logo.classList.toggle('hidden', !useTransparent))
    }

    const setMenuButtonState = (isOpen) => {
      menuButtons.forEach((button) => {
        button.setAttribute('aria-expanded', String(isOpen))
        button.setAttribute(
          'aria-label',
          button.getAttribute(isOpen ? 'data-aria-label-opened' : 'data-aria-label-closed')
        )
        button.querySelector('.icon-header-menu')?.toggleAttribute('hidden', isOpen)
        button.querySelector('.icon-header-menu-close')?.toggleAttribute('hidden', !isOpen)
      })
    }

    const setOverlayMenuOpen = (menu, isOpen) => {
      menu.classList.toggle('hidden', !isOpen)
      menu.toggleAttribute('hidden', !isOpen)
      menu.setAttribute('aria-hidden', isOpen ? 'false' : 'true')
      menu.setAttribute('data-hidden', isOpen ? 'false' : 'true')
      menu.inert = !isOpen
    }

    const setDrawerHidden = (isHidden) => {
      if (!drawer) return
      drawer.classList.toggle('hidden', isHidden)
      drawer.toggleAttribute('hidden', isHidden)
      drawer.inert = isHidden
    }

    let closeDrawerTimer = null

    const resetDrawerPosition = () => {
      drawer.style.top = ''
      drawer.style.bottom = ''
      drawer.style.height = ''
    }

    const closeDrawer = () => {
      if (!drawer) return
      drawer.classList.remove('active')
      drawer.setAttribute('aria-hidden', 'true')
      drawer.inert = true
      document.body.removeAttribute('mobile-menu-open')
      // Unlock background scrolling (both the root scroller and body).
      document.documentElement.classList.remove('overflow-hidden')
      document.body.classList.remove('overflow-hidden')
      // Fade the header back in without changing layout (avoids content jump).
      mobileHeaderRow?.classList.remove('opacity-0', 'pointer-events-none')
      mobileHeaderRow?.removeAttribute('inert')
      // Keep the open-state top offset until the slide-out finishes, otherwise the
      // drawer jumps upward mid-animation when inset-0 takes over. Then remove the
      // drawer from the layout so lazy images are not fetched while it is closed.
      window.clearTimeout(closeDrawerTimer)
      closeDrawerTimer = window.setTimeout(() => {
        resetDrawerPosition()
        setDrawerHidden(true)
      }, 300)
      setMenuButtonState(false)
    }

    const openDrawer = () => {
      if (!drawer) return
      window.clearTimeout(closeDrawerTimer)
      setDrawerHidden(false)
      // Force a layout pass so the slide-in transition still runs after display:none.
      void drawer.offsetHeight
      drawer.classList.add('active')
      drawer.setAttribute('aria-hidden', 'false')
      document.body.setAttribute('mobile-menu-open', 'true')
      // Lock background scrolling. The root <html> is the actual scroller, so
      // locking body alone doesn't stop the page (especially on mobile).
      document.documentElement.classList.add('overflow-hidden')
      document.body.classList.add('overflow-hidden')
      // Fade header out in place so page content doesn't shift upward.
      mobileHeaderRow?.classList.add('opacity-0', 'pointer-events-none')
      mobileHeaderRow?.setAttribute('inert', '')
      // Start the drawer below the announcement bar so it stays visible on top,
      // rather than covering it. Bound height to the remaining viewport so the
      // inner overflow-y scroller can actually scroll (height:auto grows with
      // content and disables overflow).
      const offset = announcementBar ? Math.max(0, Math.round(announcementBar.getBoundingClientRect().bottom)) : 0
      drawer.style.top = `${offset}px`
      drawer.style.bottom = '0'
      drawer.style.height = `calc(100dvh - ${offset}px)`
      setMenuButtonState(true)
      drawer.querySelector('a, button, input, select')?.focus()
    }

    const setSearchButtonState = (isOpen) => {
      header.querySelectorAll('[data-search]').forEach((button) => {
        button.setAttribute('aria-expanded', String(isOpen))
        button.querySelector('.icon-header-search')?.toggleAttribute('hidden', isOpen)
        button.querySelector('.icon-header-menu-close')?.toggleAttribute('hidden', !isOpen)
      })
    }

    const closeQuickSearch = () => {
      if (!quickSearch) return
      quickSearch.classList.add('hidden')
      quickSearch.setAttribute('aria-hidden', 'true')
      document.body.classList.remove('overflow-hidden')
      setSearchButtonState(false)
    }

    const openQuickSearch = () => {
      if (!quickSearch) return
      closeDrawer()
      quickSearch.classList.remove('hidden')
      quickSearch.setAttribute('aria-hidden', 'false')
      document.body.classList.add('overflow-hidden')
      setSearchButtonState(true)
      quickSearch.querySelector('[data-input]')?.focus()
    }

    header.querySelectorAll('[data-search]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault()
        openQuickSearch()
      })
    })
    quickSearch?.querySelectorAll('[data-overlay], [data-close-icon]').forEach((button) => {
      button.addEventListener('click', closeQuickSearch)
    })
    quickSearch?.querySelector('[data-clear]')?.addEventListener('click', () => {
      const input = quickSearch.querySelector('[data-input]')
      if (input) input.value = ''
      input?.focus()
    })

    menuButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault()
        if (drawer?.classList.contains('active')) closeDrawer()
        else openDrawer()
      })
    })

    drawer?.querySelector('[data-overlay]')?.addEventListener('click', closeDrawer)
    drawer
      ?.querySelectorAll('[data-mobile-nav-close]')
      .forEach((button) => button.addEventListener('click', closeDrawer))
    drawer?.querySelectorAll('select[name="country_code"], select[name="locale_code"]').forEach((select) => {
      select.addEventListener('change', () => {
        closeDrawer()
        select.form?.submit()
      })
    })

    drawer?.querySelectorAll('[data-mobile-nav-v2]').forEach((mobileNav) => {
      const tabs = [...mobileNav.querySelectorAll('[data-mobile-nav-tab]')]
      const panels = [...mobileNav.querySelectorAll('[data-mobile-nav-panel]')]

      const setActiveTab = (tabId) => {
        tabs.forEach((tab) => {
          const isActive = tab.dataset.tabId === tabId
          tab.setAttribute('aria-selected', String(isActive))
          tab.tabIndex = isActive ? 0 : -1
        })
        panels.forEach((panel) => {
          const isInactive = panel.dataset.tabId !== tabId
          panel.hidden = isInactive
          panel.classList.toggle('!hidden', isInactive)
        })
      }

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => setActiveTab(tab.dataset.tabId))
      })

      const activeTab = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]
      if (activeTab) setActiveTab(activeTab.dataset.tabId)
    })

    drawer?.querySelectorAll('[data-item="parent"]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault()
        const submenu = trigger.nextElementSibling
        if (!submenu) return
        trigger.setAttribute('aria-expanded', 'true')
        submenu.classList.remove('hidden')
        submenu.setAttribute('aria-hidden', 'false')
        submenu.querySelector('[data-item="back"]')?.focus()
      })
    })

    drawer?.querySelectorAll('[data-item="back"]').forEach((trigger) => {
      trigger.setAttribute('tabindex', '0')
      trigger.addEventListener('click', () => {
        const submenu = trigger.closest('.drawer-menu__list--sub')
        if (!submenu) return
        submenu.classList.add('hidden')
        submenu.setAttribute('aria-hidden', 'true')
        const parentTrigger = submenu.previousElementSibling
        parentTrigger?.setAttribute('aria-expanded', 'false')
        parentTrigger?.focus()
      })
    })

    const navMenus = header.querySelectorAll('[data-navigation]')
    const topLevelLinks = header.querySelectorAll(
      '[data-navigation] > .header__links-list > li > [data-link]'
    )
    const setNavigationLinkState = (activeLink) => {
      topLevelLinks.forEach((link) => {
        const isActive = link === activeLink
        link.classList.toggle('opacity-40', Boolean(activeLink) && !isActive)
        // Drive the text underline via CSS (group-[.is-nav-hover]) instead of
        // text-decoration, so it doesn't stack with the animated after-line.
        link.classList.toggle('is-nav-hover', isActive)
      })
    }

    const closeNavigation = (exception) => {
      header.querySelectorAll('[data-submenu]').forEach((menu) => {
        if (menu === exception || menu.contains(exception)) return
        setOverlayMenuOpen(menu, false)
      })
      header.querySelectorAll('[data-parent], [data-dropdown-trigger]').forEach((trigger) => {
        if (!exception || trigger.getAttribute('aria-controls') !== exception.id) {
          trigger.setAttribute('aria-expanded', 'false')
        }
      })
      if (!exception) {
        header.classList.remove('dropdown-active')
        overlay?.classList.remove('opacity-50')
        setNavigationLinkState()
        setTransparentState()
      }
    }

    const openNavigation = (trigger) => {
      const menuId = trigger.getAttribute('aria-controls')
      const menu = (menuId ? document.getElementById(menuId) : null) || trigger.nextElementSibling
      if (!menu) return

      closeNavigation(menu)
      if (
        menu.hasAttribute('data-meganav-v2') ||
        menu.hasAttribute('data-full-width-meganav') ||
        menu.dataset.depth === '1'
      ) {
        menu.style.top = `${header.getBoundingClientRect().bottom}px`
      }
      setOverlayMenuOpen(menu, true)
      const alreadyOpen = trigger.getAttribute('aria-expanded') === 'true'
      trigger.setAttribute('aria-expanded', 'true')
      if (!alreadyOpen && window.oneskin && window.oneskin.events) {
        window.oneskin.events.track('nav_expand', {
          nav_location: 'header_primary',
          device_type: window.oneskin.events.deviceType(),
        })
      }
      header.classList.add('dropdown-active')
      positionOverlay()
      overlay?.classList.add('opacity-50')
      setNavigationLinkState(trigger)
      setTransparentState()
    }

    navMenus.forEach((nav) => {
      nav.querySelectorAll('[data-parent], [data-dropdown-trigger]').forEach((trigger) => {
        trigger.addEventListener('mouseenter', () => openNavigation(trigger))
        trigger.addEventListener('focus', () => openNavigation(trigger))
        if (trigger.tagName === 'BUTTON') {
          trigger.addEventListener('click', () => openNavigation(trigger))
        }
      })

      nav.querySelectorAll(':scope > .header__links-list > li > [data-link]').forEach((link) => {
        if (link.matches('[data-parent], [data-dropdown-trigger]')) return
        link.addEventListener('mouseenter', () => {
          closeNavigation()
          setNavigationLinkState(link)
        })
        link.addEventListener('focus', () => {
          closeNavigation()
          setNavigationLinkState(link)
        })
      })
    })

    header.querySelectorAll('.support-menu__item').forEach((item) => {
      const submenu = item.querySelector('.js-submenu')
      if (!submenu) return
      const open = () => {
        submenu.classList.remove('invisible', 'opacity-0')
        submenu.classList.add('visible', 'opacity-100')
      }
      const close = () => {
        submenu.classList.add('invisible', 'opacity-0')
        submenu.classList.remove('visible', 'opacity-100')
      }
      item.addEventListener('mouseenter', open)
      item.addEventListener('mouseleave', close)
      item.addEventListener('focusin', open)
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) close()
      })
    })

    header.addEventListener('mouseleave', () => closeNavigation())
    document.addEventListener('click', (event) => {
      if (!header.contains(event.target)) closeNavigation()
    })
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Tab' && drawer?.classList.contains('active')) trapFocus(drawer, event)
      if (event.key === 'Tab' && quickSearch && !quickSearch.classList.contains('hidden')) {
        trapFocus(quickSearch, event)
      }
      if (event.key !== 'Escape') return
      closeDrawer()
      closeQuickSearch()
      closeNavigation()
    })

    header.querySelectorAll('[data-disclosure]').forEach(initHeaderDisclosure)

    if (enableStickyHeader && headerSpace) {
      // Toggle "active" sticky styling once the top sentinel leaves the viewport.
      const stickyObserver = new IntersectionObserver(([{ isIntersecting }]) => {
        header.classList.toggle('is-sticky', !isIntersecting)
        document.documentElement.classList.toggle('sticky-header-active', !isIntersecting)
      })
      stickyObserver.observe(headerSpace)
    } else {
      header.classList.remove('is-sticky')
      document.documentElement.classList.remove('sticky-header-active')
    }

    setHeaderHeight()
    setTransparentState()
    window.addEventListener('scroll', setTransparentState, { passive: true })
    new ResizeObserver(setHeaderHeight).observe(header)

    // Close the mobile drawer when crossing into the desktop nav layout (≥1025px)
    const desktopNavMq = window.matchMedia('(min-width: 1025px)')
    const handleDesktopNavChange = (event) => {
      if (event.matches) closeDrawer()
    }
    if (typeof desktopNavMq.addEventListener === 'function') {
      desktopNavMq.addEventListener('change', handleDesktopNavChange)
    } else {
      desktopNavMq.addListener(handleDesktopNavChange)
    }

    header.dataset.initialized = 'true'
  }

  const initHeaders = (root = document) => {
    root.querySelectorAll('[data-section-type="header"]').forEach(initHeader)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initHeaders())
  } else {
    initHeaders()
  }

  document.addEventListener('shopify:section:load', (event) => initHeaders(event.target))
})()
